// Bridge to the native Android UpiPay plugin. On web / non-native the caller
// falls back to a plain `upi://` deep link and manual confirmation, because
// the browser cannot receive the UPI PSP's onActivityResult payload.

export type UpiResponse = {
  status: "SUCCESS" | "FAILURE" | "PENDING";
  txnId: string;
  responseCode: string;
  approvalRefNo: string;
  txnRef: string;
  utr: string;
  raw?: string;
  resultCode?: number;
};

export type UpiPayArgs = {
  pa: string;   // payee UPI id
  pn?: string;  // payee name
  am: string;   // amount (string, e.g. "500.00")
  tr: string;   // our order id / PID
  tn?: string;  // note
  cu?: string;  // currency, default INR
};

type CapacitorWindow = Window & {
  Capacitor?: {
    isNativePlatform?: () => boolean;
    PluginHeaders?: Array<{ name?: string }>;
  };
};

function getCapacitorRuntime() {
  return typeof window !== "undefined" ? (window as CapacitorWindow).Capacitor : undefined;
}

export function isNativeUpiAvailable(): boolean {
  try {
    const cap = getCapacitorRuntime();
    return !!(cap && typeof cap.isNativePlatform === "function" && cap.isNativePlatform());
  } catch {
    return false;
  }
}

export function isNativePluginAvailable(pluginName: "App" | "ImbPayment" | "UpiPay" | string): boolean {
  try {
    const cap = getCapacitorRuntime();
    if (!cap || typeof cap.isNativePlatform !== "function" || !cap.isNativePlatform()) return false;
    return Array.isArray(cap.PluginHeaders) && cap.PluginHeaders.some((header) => header?.name === pluginName);
  } catch {
    return false;
  }
}

export async function payWithNativeUpi(args: UpiPayArgs): Promise<UpiResponse> {
  if (!isNativePluginAvailable("UpiPay")) {
    throw new Error("UpiPay native plugin is not available in this Android build.");
  }
  const { registerPlugin } = await import("@capacitor/core");
  const UpiPay = registerPlugin<{ pay: (opts: UpiPayArgs) => Promise<UpiResponse> }>("UpiPay");
  return UpiPay.pay(args);
}
